import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  cancelRental,
  fetchRentalPayments,
  cancelAdminRental,
  fetchAdminRentalPayments,
} from '../../Redux/Actions/actions';
import { RENTAL_TYPE } from '../../Views/RentalManagement/utils/rentalTypeHelpers';
import style from './CancelRentalModal.module.css';

/**
 * Modal de cancelación de contrato de alquiler. Soporta tipo regular y admin.
 *
 * Props:
 * - rentalId: string (uuid o id del contrato)
 * - propertyTitle?: string
 * - type?: 'regular' | 'admin' (default 'regular')
 * - onClose: () => void
 * - onSuccess?: (resultData) => void
 */
const CancelRentalModal = ({ rentalId, propertyTitle, type = RENTAL_TYPE.REGULAR, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const isAdmin = type === RENTAL_TYPE.ADMIN;
  const userId = useSelector((state) => state.userId);
  const rentalPaymentsRegular = useSelector((state) => state.rentalPayments) || [];
  const adminPaymentsState = useSelector((state) => state.adminRentalPayments);
  const adminPayments = Array.isArray(adminPaymentsState?.data)
    ? adminPaymentsState.data
    : Array.isArray(adminPaymentsState)
    ? adminPaymentsState
    : [];
  const rentalPayments = isAdmin ? adminPayments : rentalPaymentsRegular;
  const rentalsLoading = useSelector((state) =>
    isAdmin ? state.adminRentalsLoading : state.rentalsLoading
  );

  const today = new Date().toISOString().slice(0, 10);

  const [terminationReason, setTerminationReason] = useState('');
  const [terminationDate, setTerminationDate] = useState(today);
  const [cancelFuturePayments, setCancelFuturePayments] = useState(true);
  const [releaseProperty, setReleaseProperty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fetch de cuotas para preview exacta
  useEffect(() => {
    if (!rentalId) return;
    const fetcher = isAdmin
      ? dispatch(fetchAdminRentalPayments(rentalId))
      : dispatch(fetchRentalPayments(rentalId));
    Promise.resolve(fetcher).catch(() => {
      // si falla, simplemente no mostramos preview
    });
  }, [dispatch, rentalId, isAdmin]);

  const previewCounts = useMemo(() => {
    const items = Array.isArray(rentalPayments) ? rentalPayments : [];
    const cutoff = terminationDate;
    let futureToCancel = 0;
    let stillCollectable = 0;
    items.forEach((p) => {
      const status = ((isAdmin ? p.status : p.paymentStatus) || '').toLowerCase();
      const due = (p.dueDate || '').slice(0, 10);
      if (status === 'paid' || status === 'partial' || status === 'overdue') {
        stillCollectable += 1;
        return;
      }
      if (status === 'pending' && due && due > cutoff) {
        futureToCancel += 1;
      }
    });
    return { futureToCancel, stillCollectable };
  }, [rentalPayments, terminationDate, isAdmin]);

  const reasonTooShort = terminationReason.trim().length < 10;

  const handleSubmit = async () => {
    if (reasonTooShort) {
      toast.warn('El motivo debe tener al menos 10 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      const basePayload = {
        terminationReason: terminationReason.trim(),
        terminationDate,
        cancelFuturePayments,
        ...(userId ? { cancelledBy: userId } : {}),
      };
      let result;
      if (isAdmin) {
        // Admin no soporta releaseProperty (propiedad embebida).
        result = await dispatch(cancelAdminRental(rentalId, basePayload));
      } else {
        result = await dispatch(
          cancelRental(rentalId, {
            ...basePayload,
            releaseProperty,
          })
        );
      }

      const cancelledCount = result?.cancelledPaymentsCount ?? 0;
      const pendingCount = result?.pendingOverdueCount ?? 0;
      const propertyReleased = !!result?.propertyReleased;

      toast.success(
        `Contrato cancelado. ${cancelledCount} cuota(s) futura(s) canceladas, ${pendingCount} con deuda siguen siendo cobrables.`
      );

      if (propertyReleased) {
        toast.info(
          'La propiedad fue liberada. Recordá marcarla como cerrada (cerrado.cierre = true) antes de crear un nuevo contrato.',
          { autoClose: 8000 }
        );
      }

      if (onSuccess) onSuccess(result);
      onClose();
    } catch (error) {
      const errorCode = error?.response?.data?.errorCode;
      const message = error?.response?.data?.message || error?.message || 'Error al cancelar el contrato.';
      if (errorCode === 'RENTAL_ALREADY_TERMINATED' || errorCode === 'ADMIN_RENTAL_ALREADY_TERMINATED') {
        toast.warn('El contrato ya estaba cancelado.');
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={style.modal} onClick={onClose}>
      <div className={style.modalContent} onClick={(e) => e.stopPropagation()}>
        <h4 className={style.modalTitle}>Cancelar contrato de alquiler</h4>
        {propertyTitle && (
          <p className={style.subtitle}>{propertyTitle}</p>
        )}

        <label className={style.label}>
          Motivo de la cancelación <span className={style.required}>*</span>
          <textarea
            value={terminationReason}
            onChange={(e) => setTerminationReason(e.target.value)}
            placeholder="Ej: Rescisión anticipada por mudanza del inquilino (mín. 10 caracteres)"
            className={style.textarea}
            rows={3}
          />
        </label>

        <label className={style.label}>
          Fecha efectiva de la rescisión
          <input
            type="date"
            value={terminationDate}
            onChange={(e) => setTerminationDate(e.target.value)}
            className={style.input}
          />
        </label>

        <label className={style.checkboxRow}>
          <input
            type="checkbox"
            checked={cancelFuturePayments}
            onChange={(e) => setCancelFuturePayments(e.target.checked)}
          />
          <span>Cancelar cuotas futuras (recomendado)</span>
        </label>

        {!isAdmin && (
          <label className={style.checkboxRow}>
            <input
              type="checkbox"
              checked={releaseProperty}
              onChange={(e) => setReleaseProperty(e.target.checked)}
            />
            <span>
              Liberar la propiedad y volver a publicarla
              <small className={style.hint}>
                Si lo marcás, deberás volver a marcarla como cerrada manualmente antes de crear otro contrato.
              </small>
            </span>
          </label>
        )}

        <div className={style.preview}>
          <strong>Resumen:</strong>
          <span> Se cancelarán <b>{previewCounts.futureToCancel}</b> cuota(s) futura(s).</span>
          <span> Quedan <b>{previewCounts.stillCollectable}</b> cuota(s) pagas/parciales/vencidas que seguirán siendo cobrables.</span>
          <small className={style.hint}>Esta acción es reversible (botón Reactivar).</small>
        </div>

        <div className={style.actions}>
          <button type="button" className={style.cancelBtn} onClick={onClose} disabled={submitting}>
            Volver
          </button>
          <button
            type="button"
            className={style.confirmBtn}
            onClick={handleSubmit}
            disabled={submitting || rentalsLoading || reasonTooShort}
          >
            {submitting ? 'Cancelando...' : 'Confirmar cancelación'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelRentalModal;
